'use client'

import avatarIcon from '@public/images/avatar.svg'
import cartIcon from '@public/images/cart.svg'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { Stamp } from '@src/components/TopNavV5/Stamp'
import { useTopNavV5 } from '@src/components/TopNavV5/TopNavV5Context'
import { TopNavV5DesktopMiniMenu } from '@src/components/TopNavV5/TopNavV5Desktop/TopNavV5DesktopMiniMenu'
import type { TopNavV5MenuItem } from '@src/components/TopNavV5/TopNavV5Props'
import { configPublic } from '@src/config.public'
import { useCart } from '@src/hooks/useCart'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { getCartQuantity } from '@src/utils/cart.util'
import { cn } from '@src/utils/tailwind'

const itemClasses =
  'flex items-center cursor-pointer h-full transition-all hover:text-bodi-white text-body-md! text-nowrap'

const hoverTimeouts: Record<string, number> = {}
const hoverDelay = configPublic.general.topNavDelay

export const TopNavV5DesktopBar = () => {
  const { t } = useLocale('common')
  const { cart } = useCart()
  const quantity = getCartQuantity(cart)
  const { user, getLoginHref } = useSession()
  const loginHref = getLoginHref()
  const [accountContainerWidth, setAccountContainerWidth] = useState<number | undefined>(
    getInitialContainerWidth()
  )
  const trackSelectNavCta = useTrackSelectNavCta()
  const {
    logo,
    items,
    account,
    isExpanded,
    expandedMenu,
    setExpandedMenu,
    setExpanded,
    setAccountExpanded,
    topNavDesktopHeight,
  } = useTopNavV5()
  const accountContainerRef = useRef<HTMLDivElement>(null)
  function getInitialContainerWidth() {
    if (!user) return 302 // width when user is not logged in
    if (user?.hasBodiAccess) return 157 // width for users with Bodi access
    if (!user?.hasBodiAccess) return 300 // width for users without Bodi access
  }

  useEffect(() => {
    function updateWidth() {
      if (accountContainerRef.current) {
        setAccountContainerWidth(accountContainerRef.current.offsetWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const renderItems = (items?: TopNavV5MenuItem[]) => {
    return items
      ?.filter((item) => Boolean(item.title))
      .map((item, index) => {
        const isSelected = item.title === expandedMenu?.title && isExpanded
        const componentKey = `${item.title}-${index}`
        const isLink = item.subMenuType === 'link'
        const isMiniMenu = item.subMenuType === 'miniMenu'

        if (isLink) {
          const { href, newTab } = item.link ?? {}
          return (
            <LocalizedLink
              data-testid={`nav-item-${index}`}
              onMouseEnter={() => setExpanded(false)}
              onClick={() => {
                trackSelectNavCta({ 'cta url': href })
              }}
              key={componentKey}
              className={cn(itemClasses, 'group relative')}
              href={href ?? ''}
              rel={newTab ? 'noopener noreferrer' : ''}
              target={newTab ? '_blank' : '_self'}
            >
              {item.title}
              <Stamp text={item.stamp} />
              <div
                className={
                  'absolute bottom-0 left-0 h-[3px] w-full bg-bodi-green-bright-500 opacity-0 transition-all duration-200 group-hover:opacity-100'
                }
              ></div>
            </LocalizedLink>
          )
        }

        if (isMiniMenu) {
          return (
            <TopNavV5DesktopMiniMenu
              key={componentKey}
              componentKey={componentKey}
              item={item}
              index={index}
              itemClasses={itemClasses}
            />
          )
        }

        return (
          <button
            data-testid={`nav-item-${index}`}
            key={componentKey}
            onMouseEnter={() => {
              setExpandedMenu(item)
              hoverTimeouts[componentKey] = window.setTimeout(() => setExpanded(true), hoverDelay)
            }}
            onMouseLeave={() => {
              clearTimeout(hoverTimeouts[componentKey])
            }}
            onClick={() => {
              clearTimeout(hoverTimeouts[componentKey])
              setExpanded(!isExpanded)
            }}
            className={cn(
              itemClasses,
              'relative flex appearance-none items-center gap-2 font-normal',
              {
                'text-bodi-white font-medium': isSelected,
              }
            )}
          >
            {item.title}
            <Stamp text={item.stamp} />
            <div
              className={cn(
                'absolute bottom-0 left-0 h-[3px] w-full bg-bodi-green-bright-500 transition-opacity',
                {
                  'opacity-100': isSelected,
                  'opacity-0': !isSelected,
                }
              )}
            ></div>
          </button>
        )
      })
  }

  return (
    <div
      style={{
        height: `${topNavDesktopHeight}px`,
      }}
      className="relative z-50 w-full bg-bodi-black-text px-4 font-medium text-bodi-gray-butters"
    >
      <div className="flex h-full items-center justify-between">
        {logo?.src && (
          <div
            className="flex h-full items-center"
            style={accountContainerWidth ? { width: accountContainerWidth } : undefined}
          >
            <LocalizedLink
              onMouseEnter={() => setExpanded(false)}
              onClick={() => {
                trackSelectNavCta({ 'cta url': logo?.href })
              }}
              href={logo?.href ?? ''}
              className="flex h-full shrink-0 items-center"
            >
              <Image
                src={logo.src}
                width={logo?.desktopWidth}
                height={logo?.desktopHeight}
                alt={logo?.altText ?? ''}
              />
            </LocalizedLink>
          </div>
        )}
        <div className="flex h-full items-center gap-6 px-4">{renderItems(items)}</div>
        <div
          onMouseEnter={() => setExpanded(false)}
          className="flex h-full shrink-0 items-center justify-end gap-2"
          ref={accountContainerRef}
        >
          <Link data-testid="cartBtn" href="#cart" className="relative mt-0.5 mr-2" scroll={false}>
            <Image src={cartIcon} width={24} height={24} alt={t('view-cart')} />
            {quantity ? (
              <div
                className={cn(
                  'absolute top-0 left-full -mt-3 ml-[-18px] flex min-h-[24px] min-w-[24px] items-center justify-center rounded-full border-2 border-bodi-black-text bg-bodi-green-bright-500 text-body-xs font-semibold text-bodi-black',
                  { 'px-1.5': quantity > 99 }
                )}
              >
                {quantity > 999 ? '999' : quantity}
              </div>
            ) : null}
          </Link>

          {!user ? (
            <a href={loginHref}>
              <Button
                size="small"
                variant="black"
                className="border border-solid border-bodi-gray-medium"
                onClick={() => {
                  trackSelectNavCta({ 'cta url': loginHref })
                }}
                data-testid="loginBtn"
              >
                {t('login')}
              </Button>
            </a>
          ) : (
            <button
              onClick={() => {
                setAccountExpanded(true)
              }}
              data-testid="accountBtn"
              className="flex items-center gap-2 font-medium text-bodi-gray-butters"
            >
              <Image src={avatarIcon} width={24} height={24} alt="Account icon" />
              {t('my-account')}
            </button>
          )}
          {!user?.hasBodiAccess && account?.bodiCta?.title && account?.bodiCta?.href && (
            <LocalizedLink
              onClick={() => {
                trackSelectNavCta({ 'cta url': account?.bodiCta?.href })
              }}
              data-testid="bodiBtn"
              href={account?.bodiCta?.href ?? ''}
              rel={account?.bodiCta?.newTab ? 'noopener noreferrer' : ''}
              target={account?.bodiCta?.newTab ? '_blank' : '_self'}
            >
              <Button className="[&>div]:px-8" size="small">
                {account?.bodiCta?.title}
              </Button>
            </LocalizedLink>
          )}
        </div>
      </div>
    </div>
  )
}
